import { inject, Injectable } from '@angular/core';
import { ItemConfirmation } from '../types/item-confirmation';
import { ApiDataAccess, Dictionary, HttpApiMutationRepository } from '@softline/core';
import {
  SOFTLINE_API_SALES_DOCUMENT_ITEM_ATTRIBUTES,
  SOFTLINE_API_SALES_DOCUMENT_ITEM_BY_ID,
  SOFTLINE_API_SALES_DOCUMENT_ITEM_CONFIRMATIONS,
  SOFTLINE_API_SALES_DOCUMENT_ITEM_DISCOUNT_BY_ID,
  SOFTLINE_API_SALES_DOCUMENT_ITEM_DISCOUNTS,
  SOFTLINE_API_SALES_DOCUMENT_ITEM_RECALCULATE_PRICE,
  SOFTLINE_API_SALES_DOCUMENT_ITEMS,
  SOFTLINE_API_SALES_DOCUMENT_ITEMS_ADD_TO_NEW_FLATRATE,
  SOFTLINE_API_SALES_DOCUMENT_ITEMS_ADD_TO_NEW_SECTION,
  SOFTLINE_API_SALES_DOCUMENT_ITEMS_DELETE,
  SOFTLINE_API_SALES_DOCUMENT_ITEMS_MOVE,
  SOFTLINE_API_SALES_DOCUMENT_FULFILL,
  SOFTLINE_API_SALES_DOCUMENT_ITEMS_TEMPLATE,
  SOFTLINE_API_SALES_DOCUMENT_ITEMS_UNDO_FLATRATE,
  SOFTLINE_API_SALES_DOCUMENT_ITEM_RETURNING_ITEM_REFERENCES,
  SOFTLINE_API_SALES_DOCUMENT_ITEM_UNIT_COMPOSITION,
  SOFTLINE_API_SALES_DOCUMENT_ITEM_UNIT_COMPOSITION_CALCULATE,
} from '../sales.api';
import {
  ReturningItemReferenceCandidate,
  ReturningItemReferenceQuery,
} from '../types/returning-item-reference';
import { UnitComposition, UnitCompositionRequest } from '../types/unit-composition';
import { SalesDocumentItem, SalesDocumentItemPrice } from '../types/sales-document-item';
import { isText } from '../grid/positions/sales-document-item-predicates.function';
import {
  CreateSalesDocumentItemRequest,
  MoveSalesDocumentItemRequest,
  PatchPriceRequest,
  PatchSalesDocumentItemDiscountRequest,
  PatchSalesDocumentItemRequest,
  RecalculatePriceRequest,
  AddToNewParentRequest,
  RemoveSalesDocumentItemsRequest,
  FulfillSalesDocumentItemRequest,
  SalesDocumentItemTemplateRequest,
  SalesDocumentItemTemplateResponse,
  SalesDocumentItemUpdateResponse,
} from '../types/sales-document-item-requests';

export interface SalesDocumentItemParams {
  entity: SalesDocumentItem;
  documentId: string;
  itemId?: string;
}

function toPriceRequest(
  price: SalesDocumentItemPrice | null | undefined
): PatchPriceRequest | undefined {
  if (!price) return undefined;
  const request: PatchPriceRequest = {};
  if (price.price != null) request.price = price.price;
  return Object.keys(request).length > 0 ? request : undefined;
}

@Injectable()
export class SalesDocumentItemMutationRepository extends HttpApiMutationRepository<
  SalesDocumentItem,
  SalesDocumentItemParams
> {
  constructor() {
    super(inject(ApiDataAccess), SOFTLINE_API_SALES_DOCUMENT_ITEM_BY_ID);
  }

  protected override getPathParams(
    params: SalesDocumentItemParams
  ): Dictionary<unknown> | undefined {
    return { id: params.documentId, itemId: params.itemId ?? '' };
  }

  override async create(
    value: SalesDocumentItem,
    params: SalesDocumentItemParams
  ): Promise<SalesDocumentItem> {
    return this.createItem(this.toCreateRequest(value), params.documentId).then(
      (response) => response.change?.items?.[0] ?? value
    );
  }

  override async update(
    value: SalesDocumentItem,
    params: SalesDocumentItemParams
  ): Promise<SalesDocumentItem> {
    return this.patch(value, { ...params, itemId: params.itemId ?? value.id });
  }

  override async patch(
    patch: Partial<SalesDocumentItem>,
    params: SalesDocumentItemParams
  ): Promise<SalesDocumentItem> {
    if (!params.itemId) {
      throw new Error('SalesDocumentItemMutationRepository.patch requires itemId');
    }
    return this.patchItem(
      this.toPatchRequest(patch),
      params.documentId,
      params.itemId
    ).then(
      (response) => response.change?.items?.[0] ?? ({ ...patch } as SalesDocumentItem)
    );
  }

  override async delete(params: SalesDocumentItemParams): Promise<SalesDocumentItem> {
    if (!params.itemId) {
      throw new Error('SalesDocumentItemMutationRepository.delete requires itemId');
    }
    await this.deleteItems({ sourceIds: [params.itemId] }, params.documentId);
    return params.entity;
  }

  createItem(
    body: CreateSalesDocumentItemRequest,
    documentId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<
      CreateSalesDocumentItemRequest,
      SalesDocumentItemUpdateResponse
    >({ path: SOFTLINE_API_SALES_DOCUMENT_ITEMS, pathParams: { id: documentId } }, body);
  }

  loadItemTemplate(
    body: SalesDocumentItemTemplateRequest,
    documentId: string
  ): Promise<SalesDocumentItemTemplateResponse> {
    return this.dataAccess.create<
      SalesDocumentItemTemplateRequest,
      SalesDocumentItemTemplateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEMS_TEMPLATE,
        pathParams: { id: documentId },
      },
      body
    );
  }

  /** Creates the new position by fulfilling ("abbuchen") a source position of an
      open document; the response is the usual item mutation response, so no
      separate create call follows. */
  fulfillItem(
    body: FulfillSalesDocumentItemRequest,
    documentId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<
      FulfillSalesDocumentItemRequest,
      SalesDocumentItemUpdateResponse
    >(
      { path: SOFTLINE_API_SALES_DOCUMENT_FULFILL, pathParams: { id: documentId } },
      body
    );
  }

  /** Pre-check of a planned PATCH (same body). Empty = nothing to object. */
  checkItemConfirmations(
    body: PatchSalesDocumentItemRequest,
    documentId: string,
    itemId: string
  ): Promise<ItemConfirmation[]> {
    return this.dataAccess.create<PatchSalesDocumentItemRequest, ItemConfirmation[]>(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_CONFIRMATIONS,
        pathParams: { id: documentId, itemId },
      },
      body
    );
  }

  /** Source-document positions a Retourbezug can reference. Pure query (POST
      only for the body) — `{}` lets the backend pick the candidates itself. */
  searchReturningItemReferences(
    body: ReturningItemReferenceQuery,
    documentId: string,
    itemId: string
  ): Promise<ReturningItemReferenceCandidate[]> {
    return this.dataAccess.create<
      ReturningItemReferenceQuery,
      ReturningItemReferenceCandidate[]
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_RETURNING_ITEM_REFERENCES,
        pathParams: { id: documentId, itemId },
      },
      body
    );
  }

  /** Removes (`active: false`) or restores an attribute the boolean item flags cannot
   *  reach (frontend_answers §10b). `key` is the key from the item's `attributes[]`. */
  patchItemAttribute(
    body: { key: string; active: boolean },
    documentId: string,
    itemId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.patch<
      { key: string; active: boolean },
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_ATTRIBUTES,
        pathParams: { id: documentId, itemId },
      },
      body
    );
  }

  patchItem(
    body: PatchSalesDocumentItemRequest,
    documentId: string,
    itemId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.patch<
      PatchSalesDocumentItemRequest,
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_BY_ID,
        pathParams: { id: documentId, itemId },
      },
      body
    );
  }

  deleteItems(
    body: RemoveSalesDocumentItemsRequest,
    documentId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<
      RemoveSalesDocumentItemsRequest,
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEMS_DELETE,
        pathParams: { id: documentId },
      },
      body
    );
  }

  moveItems(
    body: MoveSalesDocumentItemRequest,
    documentId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<
      MoveSalesDocumentItemRequest,
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEMS_MOVE,
        pathParams: { id: documentId },
      },
      body
    );
  }

  loadUnitComposition(documentId: string, itemId: string): Promise<UnitComposition> {
    return this.dataAccess.get<UnitComposition>({
      path: SOFTLINE_API_SALES_DOCUMENT_ITEM_UNIT_COMPOSITION,
      pathParams: { id: documentId, itemId },
    });
  }

  /** Preview: distributes/sums the given state without touching the item. */
  calculateUnitComposition(
    body: UnitCompositionRequest,
    documentId: string,
    itemId: string
  ): Promise<UnitComposition> {
    return this.dataAccess.create<UnitCompositionRequest, UnitComposition>(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_UNIT_COMPOSITION_CALCULATE,
        pathParams: { id: documentId, itemId },
      },
      body
    );
  }

  /** Applies the composition to the item — send the SAME body as the last calculate. */
  updateUnitComposition(
    body: UnitCompositionRequest,
    documentId: string,
    itemId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.update<
      UnitCompositionRequest,
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_UNIT_COMPOSITION,
        pathParams: { id: documentId, itemId },
      },
      body
    );
  }

  recalculatePrice(
    body: RecalculatePriceRequest,
    documentId: string,
    itemId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<
      RecalculatePriceRequest,
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_RECALCULATE_PRICE,
        pathParams: { id: documentId, itemId },
      },
      body
    );
  }

  addToNewSection(
    body: AddToNewParentRequest,
    documentId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<AddToNewParentRequest, SalesDocumentItemUpdateResponse>(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEMS_ADD_TO_NEW_SECTION,
        pathParams: { id: documentId },
      },
      body
    );
  }

  addToNewFlatrate(
    body: RemoveSalesDocumentItemsRequest,
    documentId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<
      RemoveSalesDocumentItemsRequest,
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEMS_ADD_TO_NEW_FLATRATE,
        pathParams: { id: documentId },
      },
      body
    );
  }

  undoFlatrate(
    documentId: string,
    itemId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<Record<string, never>, SalesDocumentItemUpdateResponse>(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEMS_UNDO_FLATRATE,
        pathParams: { id: documentId, itemId },
      },
      {}
    );
  }

  insertItemDiscount(
    body: PatchSalesDocumentItemDiscountRequest,
    documentId: string,
    itemId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.create<
      PatchSalesDocumentItemDiscountRequest,
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_DISCOUNTS,
        pathParams: { id: documentId, itemId },
      },
      body
    );
  }

  updateItemDiscount(
    body: PatchSalesDocumentItemDiscountRequest,
    documentId: string,
    itemId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.patch<
      PatchSalesDocumentItemDiscountRequest,
      SalesDocumentItemUpdateResponse
    >(
      {
        path: SOFTLINE_API_SALES_DOCUMENT_ITEM_DISCOUNT_BY_ID,
        pathParams: { id: documentId, itemId, conditionId: body.conditionId },
      },
      body
    );
  }

  deleteItemDiscount(
    documentId: string,
    itemId: string,
    conditionId: string
  ): Promise<SalesDocumentItemUpdateResponse> {
    return this.dataAccess.delete<SalesDocumentItemUpdateResponse>({
      path: SOFTLINE_API_SALES_DOCUMENT_ITEM_DISCOUNT_BY_ID,
      pathParams: { id: documentId, itemId, conditionId },
    });
  }

  toCreateRequest(item: SalesDocumentItem): CreateSalesDocumentItemRequest {
    return {
      productId: item.product?.id,
      quantity: item.salesQuantity,
      description: item.description ?? undefined,
    };
  }

  toPatchRequest(patch: Partial<SalesDocumentItem>): PatchSalesDocumentItemRequest {
    if (isText(patch as SalesDocumentItem)) {
      return {
        description: patch.description ?? undefined,
      };
    }
    // no salesUnitId: the backend accepts and ignores it (frontend_answers §6)
    return {
      consumerUnitId: patch.consumerUnit?.id,
      consumerQuantity: patch.consumerQuantity,
      salesQuantity: patch.salesQuantity,
      description: patch.description ?? undefined,
      stockId: patch.stock?.id,
      price: toPriceRequest(patch.price),
    };
  }
}
